import {
  PANES_IMAGE_URL,
  PASTAS_IMAGE_URL,
  PIZZAS_IMAGE_URL,
  POSTRES_IMAGE_URL,
} from '../utils/headMeta'

/** Familia de recetas: define el color de cada tomo, como en el mockup. */
export type Familia = 'panes' | 'pizzas' | 'pastas' | 'postres'

export interface Tomo {
  numeral: string
  familia: Familia
  titulo: string
  tagline: string
}

export interface Bono {
  numero: string
  titulo: string
  descripcion: string
  formato: string
}

export interface Faq {
  q: string
  a: string
}

export const PRODUCT_NAME = 'KETO 4P — 500 Recetas Keto'

export const PRICE = {
  previous: '$29.990',
  current: '$9.990',
  note: 'Precio en pesos argentinos · Pago único',
}

/** Los cuatro números que sostienen la oferta (hero + oferta + barra fija). */
export const HERO_STATS = [
  { valor: '6', etiqueta: 'TOMOS DIGITALES' },
  { valor: '500', etiqueta: 'RECETAS KETO' },
  { valor: '5', etiqueta: 'BONOS EXCLUSIVOS' },
]

/** Sellos de confianza que se repiten a lo largo de la página. */
export const TRUST_BADGES = [
  '100% KETO',
  'SIN HARINAS NI AZÚCARES',
  '500 RECETAS',
  '6 TOMOS DIGITALES',
  '5 BONOS EXCLUSIVOS',
  'ACCESO DIGITAL',
]

/** El problema real: aburrimiento y renuncia. Sin promesas médicas. */
export const PROBLEMA = [
  'Comés lo mismo cinco días seguidos y ya no lo soportás.',
  'Extrañás el pan, la pizza, los fideos y el postre del domingo.',
  'Buscás recetas sueltas en internet y la mitad no sale bien.',
  'Cocinar keto te parece caro, complicado y siempre igual.',
  'Aguantás unas semanas, te aburrís y abandonás.',
]

export const SOLUCION = [
  'Abrís el tomo que se te antoje y elegís entre cientos de opciones.',
  'Pan, pizza, pasta y postre vuelven a la mesa, en versión keto.',
  'Recetas ordenadas por familia, con ingredientes y pasos claros.',
  'Todo sin harinas ni azúcares: seguís en cetosis comiendo rico.',
  'Variedad suficiente para no repetir un plato durante meses.',
]

/** Las 4 P del producto: panes, pizzas, pastas y postres. */
export const FAMILIAS: Array<{
  familia: Familia
  nombre: string
  claim: string
  descripcion: string
  imagen: string
  ejemplos: string[]
}> = [
  {
    familia: 'panes',
    nombre: 'PANES',
    claim: 'El pan de todos los días, sin harinas',
    descripcion:
      'Panes artesanales, esponjosos y con miga de verdad. Para el desayuno, el sándwich del mediodía y la tostada de la noche.',
    imagen: PANES_IMAGE_URL,
    ejemplos: ['Pan de molde keto', 'Panes de semillas', 'Focaccia', 'Pan para sándwich'],
  },
  {
    familia: 'pizzas',
    nombre: 'PIZZAS',
    claim: 'Volvé a la pizza del viernes',
    descripcion:
      'Masas keto que se sostienen, se doran y se comen con la mano. Con todo el queso, sin la harina.',
    imagen: PIZZAS_IMAGE_URL,
    ejemplos: ['Masa clásica', 'Pizza de sartén', 'Fugazzeta', 'Calzone keto'],
  },
  {
    familia: 'pastas',
    nombre: 'PASTAS',
    claim: 'Fideos y salsas que llenan de verdad',
    descripcion:
      'Pastas keto cremosas, con salsas de sabor real. El plato que creías imposible dentro del plan.',
    imagen: PASTAS_IMAGE_URL,
    ejemplos: ['Fettuccine keto', 'Lasaña', 'Ñoquis', 'Salsas cremosas'],
  },
  {
    familia: 'postres',
    nombre: 'POSTRES',
    claim: 'Cerrá el día con algo dulce',
    descripcion:
      'Postres irresistibles endulzados sin azúcar. Para la merienda, la visita y el antojo de la noche.',
    imagen: POSTRES_IMAGE_URL,
    ejemplos: ['Brownies', 'Cheesecake', 'Torta de chocolate', 'Helados keto'],
  },
]

/** Los 6 tomos, tal como aparecen en las portadas del mockup oficial. */
export const TOMOS: Tomo[] = [
  {
    numeral: 'TOMO I',
    familia: 'panes',
    titulo: 'PANES',
    tagline: 'Panes keto artesanales, esponjosos y sin culpa',
  },
  {
    numeral: 'TOMO II',
    familia: 'panes',
    titulo: 'PANES',
    tagline: 'Más panes, bollos y masas saladas keto',
  },
  {
    numeral: 'TOMO III',
    familia: 'pizzas',
    titulo: 'PIZZAS',
    tagline: 'Pizzas keto artesanales, sabrosas y sin culpa',
  },
  {
    numeral: 'TOMO IV',
    familia: 'pastas',
    titulo: 'PASTAS',
    tagline: 'Pastas keto artesanales, cremosas y llenas de sabor',
  },
  {
    numeral: 'TOMO V',
    familia: 'postres',
    titulo: 'POSTRES',
    tagline: 'Postres keto irresistibles, dulces y sin culpa',
  },
  {
    numeral: 'TOMO VI',
    familia: 'postres',
    titulo: 'POSTRES',
    tagline: 'Más postres, tortas y dulces keto para compartir',
  },
]

/** Los 5 bonos exclusivos, en PDF. */
export const BONOS: Bono[] = [
  {
    numero: '01',
    titulo: 'Lista de Compras Keto',
    descripcion: 'Qué poner en el carrito y qué dejar afuera, ordenado por góndola.',
    formato: 'EN PDF',
  },
  {
    numero: '02',
    titulo: 'Guía Rápida de Ingredientes Keto',
    descripcion: 'Las harinas, endulzantes y grasas que sí funcionan, explicadas simple.',
    formato: 'EN PDF',
  },
  {
    numero: '03',
    titulo: 'Sustituciones Inteligentes para Cocinar Mejor',
    descripcion: 'Tabla completa para reemplazar cualquier ingrediente sin arruinar la receta.',
    formato: 'EN PDF',
  },
  {
    numero: '04',
    titulo: 'Meal Prep Keto — Planificación Semanal',
    descripcion: 'Cómo cocinar una vez y tener la semana resuelta.',
    formato: 'EN PDF',
  },
  {
    numero: '05',
    titulo: 'Recetas Extra Sorpresa',
    descripcion: 'Un extra que no está en los tomos, de regalo con tu compra.',
    formato: 'EN PDF',
  },
]

export const AUDIENCE = [
  'Ya arrancaste keto y querés una forma más fácil de sostenerlo sin aburrirte.',
  'Extrañás el pan, la pizza, la pasta o el postre y no querés resignarlos.',
  'Cocinás para tu familia y necesitás platos que puedan disfrutar todos.',
  'Querés variedad real para elegir qué comer sin perder tiempo buscando recetas sueltas.',
]

export const OFFER_INCLUDES = [
  '6 tomos digitales',
  '500 recetas keto',
  '5 bonos exclusivos',
  'PDFs para leer desde tu celular, tablet o computadora',
]

export const FAQS: Faq[] = [
  {
    q: '¿Qué recibo exactamente?',
    a: 'Los 6 tomos digitales de KETO 4P con 500 recetas de panes, pizzas, pastas y postres, más los 5 bonos exclusivos en PDF.',
  },
  {
    q: '¿Es material digital o me llega un libro impreso?',
    a: 'Es 100% digital. Después de la compra accedés a los archivos y los abrís en el celular, la tablet o la computadora.',
  },
  {
    q: '¿Cuándo lo recibo?',
    a: 'El acceso es digital y se entrega apenas se confirma el pago, así que podés cocinar la primera receta el mismo día.',
  },
  {
    q: '¿Las recetas son realmente keto?',
    a: 'Sí: todas están formuladas sin harinas de trigo ni azúcares añadidos, con ingredientes compatibles con una alimentación keto.',
  },
  {
    q: '¿Necesito ser experto en cocina?',
    a: 'No. Cada receta indica ingredientes y pasos de forma clara, y los bonos de sustituciones e ingredientes te sacan las dudas.',
  },
  {
    q: '¿Los ingredientes son difíciles de conseguir?',
    a: 'Se trabaja con ingredientes keto habituales. La Lista de Compras y la Guía de Ingredientes te indican qué comprar y cómo reemplazar lo que no encuentres.',
  },
  {
    q: '¿Sirve si cocino para toda la familia?',
    a: 'Sí. Pan, pizza, pastas y postres son platos que comen todos, así que no tenés que cocinar dos veces.',
  },
  {
    q: '¿El pago es único?',
    a: 'Sí, es un pago único. No hay suscripción ni cobros recurrentes.',
  },
  {
    q: '¿Cómo hago si tengo un problema con mi compra?',
    a: 'Podés escribirnos por los contactos que están al pie de la página y te ayudamos con tu acceso.',
  },
]
